import { useEffect, useRef } from "react";
import {
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from "react-router-dom";
import { hasAnyAppAccess } from "@/lib/roles";
import { useAuth } from "@/lib/useAuth";
import { useOrgPath } from "@/lib/useOrgNav";
import { NotFoundPage } from "@/pages/Stubs";
import type { Role } from "@/lib/types";

/**
 * Loading screen while auth is resolving.
 */
export function LoadingScreen() {
  return (
    <div
      style={{
        display: "grid",
        placeItems: "center",
        minHeight: "100vh",
        color: "var(--c-text-3)",
      }}
    >
      Loading…
    </div>
  );
}

/**
 * Gate: requires an authenticated user. Unauthenticated → /login.
 * Users flagged `must_change_password` are shunted to the change-
 * password screen until they comply — the only route they can reach.
 *
 * Preserves the original `pathname + search` in `state.from` so the
 * Login page can send the user back to the URL they actually wanted
 * after signing in (instead of dumping them on the default Overview).
 */
export function RequireAuth() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }
  if (user.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }
  return <Outlet />;
}

/** Only accept URLs whose `:orgPublicId` param is a well-formed
 *  8-digit number starting with a non-zero digit (per migration 0013
 *  constraints). Anything else is obvious garbage and routes to
 *  NotFound rather than kicking off a failed membership lookup. */
function isValidPublicId(raw: string | undefined): raw is string {
  return typeof raw === "string" && /^[1-9]\d{7}$/.test(raw);
}

/**
 * Gate: wraps every authenticated app route and makes the URL's
 * `:orgPublicId` segment the source of truth for org scope.
 *
 *   - ID matches the JWT's active org      → render children
 *   - ID is in user.orgs but not active    → call switchOrg(orgId),
 *                                             block until JWT is
 *                                             re-scoped, then render
 *   - ID not in user.orgs                  → bounce home to the
 *                                             user's default org
 *                                             Overview (the URL
 *                                             might be a copy-paste
 *                                             from someone else or
 *                                             a stale bookmark).
 */
export function OrgGuard() {
  const { user, switchOrg } = useAuth();
  const params = useParams<{ orgPublicId: string }>();
  const raw = params.orgPublicId;
  const switchAttemptedFor = useRef<string | null>(null);

  const publicIdInt = isValidPublicId(raw) ? Number(raw) : null;
  const membership =
    publicIdInt != null
      ? user?.orgs.find((o) => o.org_public_id === publicIdInt)
      : undefined;
  const needsSwitch =
    user != null && membership != null && user.org_id !== membership.org_id;

  useEffect(() => {
    if (!needsSwitch || !membership) return;
    if (switchAttemptedFor.current === String(publicIdInt)) return;
    switchAttemptedFor.current = String(publicIdInt);
    void switchOrg(membership.org_id);
  }, [needsSwitch, membership, publicIdInt, switchOrg]);

  if (!user) return <LoadingScreen />;

  if (publicIdInt == null) {
    // Malformed ID in URL — not a known route for us.
    return <NotFoundPage />;
  }

  if (!membership) {
    // The user pasted a URL for an org they're not in. Show an
    // explicit "no access" page instead of silently bouncing — per
    // user 2026-05-05, the silent redirect was confusing because
    // the URL just changed without explanation.
    return <NoOrgAccessPage requestedPublicId={publicIdInt} />;
  }

  if (needsSwitch) {
    return <LoadingScreen />;
  }

  return <Outlet />;
}

/** Role gate inside an already-validated OrgGuard subtree. */
export function RequireRole({ allow }: { allow: Role[] }) {
  const { user } = useAuth();
  const params = useParams<{ orgPublicId: string }>();
  if (!user) return <LoadingScreen />;
  if (!allow.includes(user.role)) {
    const pid = params.orgPublicId ?? user.org_public_id;
    return <Navigate to={pid ? `/${pid}/overview` : "/login"} replace />;
  }
  return <Outlet />;
}

/** Writer gate for app-scoped sections (Rules / Campaign Studio /
 *  Autopilot). Passes for account writers (admin / ads_manager) OR a
 *  per-app writer whose account role is 'viewer' (§51 — has_app_write).
 *  The backend enforces the specific target app per request. */
export function RequireWriter() {
  const { user } = useAuth();
  const params = useParams<{ orgPublicId: string }>();
  if (!user) return <LoadingScreen />;
  const canWrite =
    user.role === "admin" ||
    user.role === "ads_manager" ||
    !!user.has_app_write;
  if (!canWrite) {
    const pid = params.orgPublicId ?? user.org_public_id;
    return <Navigate to={pid ? `/${pid}/overview` : "/login"} replace />;
  }
  return <Outlet />;
}

/**
 * Root redirect: `/` → `/<active-org-public-id>/overview` once auth
 * is resolved. Unauthenticated users bounce to /login.
 *
 * Исключение — финменеджер, которому не выдано ни одного приложения:
 * его рабочая поверхность это счёт, а Overview для него пустой экран.
 * С выданной рекламой он обычный участник и заходит туда же, куда все.
 */
export function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password)
    return <Navigate to="/change-password" replace />;
  if (user.org_public_id != null) {
    const home =
      user.role === "finance_manager" && !hasAnyAppAccess(user)
        ? `/${user.org_public_id}/settings?tab=billing`
        : `/${user.org_public_id}/overview`;
    return <Navigate to={home} replace />;
  }
  return <Navigate to="/login" replace />;
}

/**
 * Surface for "URL points at an org you're not a member of". Used
 * by `OrgGuard` when `:orgPublicId` resolves but isn't in the
 * user's `orgs` array — typically a copy-paste from a colleague's
 * link, or a stale bookmark after the user lost access. Better than
 * a silent redirect because the user actually understands why their
 * URL didn't open.
 */
function NoOrgAccessPage({ requestedPublicId }: { requestedPublicId: number }) {
  const { user } = useAuth();
  const home =
    user?.org_public_id != null ? `/${user.org_public_id}/overview` : "/";
  return (
    <div
      style={{
        display: "grid",
        placeItems: "center",
        minHeight: "100vh",
        padding: 32,
      }}
    >
      <div
        style={{
          maxWidth: 480,
          textAlign: "center",
          background: "var(--c-surface)",
          border: "1px solid var(--c-border)",
          borderRadius: 12,
          padding: "32px 28px",
          boxShadow: "var(--sh-1)",
        }}
      >
        <h1
          className="serif"
          style={{
            fontSize: 26,
            fontWeight: 400,
            letterSpacing: "-0.01em",
            margin: 0,
            marginBottom: 10,
          }}
        >
          No access to this organization
        </h1>
        <p
          className="muted"
          style={{ fontSize: 14, lineHeight: 1.55, marginBottom: 22 }}
        >
          The link points to organization <code>{requestedPublicId}</code>,
          and your account isn&apos;t a member there. If a teammate sent you
          this URL, ask them to invite you from their Settings → Users.
        </p>
        <a
          href={home}
          className="btn primary"
          style={{ display: "inline-flex", textDecoration: "none" }}
        >
          Back to my workspace
        </a>
      </div>
    </div>
  );
}

/**
 * Forward legacy org-less URLs (/overview, /settings/…) to the new
 * public_id-scoped ones. Catches old pasted bookmarks.
 *
 * Same `from` preservation as RequireAuth so a pasted legacy URL
 * (e.g. /ads from a bookmark) survives the login bounce + comes
 * back to /<slug>/ads after auth.
 */
export function LegacyRedirect() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }
  if (user.must_change_password)
    return <Navigate to="/change-password" replace />;
  if (user.org_public_id == null) return <Navigate to="/login" replace />;
  return (
    <Navigate
      to={`/${user.org_public_id}${location.pathname}${location.search}`}
      replace
    />
  );
}

/**
 * Settings collapse rewrite: a single `/settings` route with a
 * horizontal tab strip + ?tab=X URL state. Old per-page sub-routes
 * (`/settings/integrations`, `/settings/users`, etc.) redirect to
 * the new tab so existing bookmarks survive. EventMapping stays as
 * a deep route since it's drill-in from a card, not a tab.
 *
 * Путь строится АБСОЛЮТНЫЙ, через тот же `useOrgPath`, которым ходят все
 * рабочие ссылки приложения. Здесь стояло
 * `<Navigate to="../settings?tab=X" relative="path" />`, и все пять
 * переходов вели в 404: `relative="path"` считает `..` от адреса в строке
 * браузера, а не от места маршрута в дереве, поэтому с
 * `/<org>/settings/integrations` срезался один сегмент вместо всего
 * маршрута и получалось `/<org>/settings/settings` — такого маршрута нет,
 * ловил `*`. Жило с 08.05.2026 по 07.08.2026 и ломало пункт Profile в
 * боковом меню, баннер маппинга и обе хлебные крошки Event Mapping.
 *
 * Относительной арифметики здесь больше нет — ломаться нечему.
 */
export function SettingsTabRedirect({ tab }: { tab: string }) {
  const orgPath = useOrgPath();
  return <Navigate to={orgPath(`/settings?tab=${tab}`)} replace />;
}
